import { useEffect, useMemo, useState } from "react";
import BudgetModuleListCard from "../components/BudgetModuleListCard";
import BudgetModulePageShell from "../components/BudgetModulePageShell";
import { AppLoader, ButtonLoader } from "../components/Loaders";
import {
  assignDepartmentPerson,
  getReferenceData,
  getWorkspace,
  removeDepartmentPerson,
  saveWorkspace,
  updateDepartmentPerson,
} from "../api/budgetPortal";
import { useAuth } from "../state/AuthContext";
import { useBudgetModuleYears } from "../hooks/useBudgetModuleYears";
import {
  buildEnabledDepartmentsMap,
  buildPlannerWorkspaceRows,
  getDepartmentCardsFromRows,
  getPlannerEmptyStateMessage,
} from "../utils/budgetWorkspace";
import { isMainOrganizationUser } from "../utils/userRole";

const BudgetModuleDepartmentsPage = () => {
  const { token, user } = useAuth();
  const isMainUser = isMainOrganizationUser(user);
  const { yearCards, selectedYear, setSelectedYear, loading, error } =
    useBudgetModuleYears(token);
  const [workspaceData, setWorkspaceData] = useState(null);
  const [workspaceRows, setWorkspaceRows] = useState([]);
  const [departmentDetails, setDepartmentDetails] = useState(null);
  const [workspaceLoading, setWorkspaceLoading] = useState(false);
  const [workspaceError, setWorkspaceError] = useState("");
  const [assignmentError, setAssignmentError] = useState("");
  const [assignmentSuccess, setAssignmentSuccess] = useState("");
  const [selectedDepartment, setSelectedDepartment] = useState(0);
  const [assigning, setAssigning] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [syncingDepartments, setSyncingDepartments] = useState(false);
  const [lockingDepartments, setLockingDepartments] = useState(false);
  const [editingAssignment, setEditingAssignment] = useState(null);
  const [editing, setEditing] = useState(false);
  const [assignmentForm, setAssignmentForm] = useState({
    name: "",
    email: "",
  });
  const [editForm, setEditForm] = useState({
    name: "",
    password: "",
  });

  useEffect(() => {
    if (!selectedYear) {
      setWorkspaceData(null);
      setWorkspaceRows([]);
      setDepartmentDetails(null);
      setWorkspaceLoading(false);
      return;
    }

    const loadWorkspace = async () => {
      setWorkspaceLoading(true);
      setWorkspaceError("");

      try {
        const [workspaceResponse, referenceResponse] = await Promise.all([
          getWorkspace(token, selectedYear),
          getReferenceData(token),
        ]);
        const workspace = workspaceResponse?.data ?? null;
        setWorkspaceData(workspace);
        setWorkspaceRows(
          buildPlannerWorkspaceRows(
            workspace?.workspace?.budget_rows ?? [],
            referenceResponse?.data?.vicode_list ?? [],
          ),
        );
        setDepartmentDetails(
          workspace?.workspace?.department_details ?? null,
        );
      } catch (err) {
        setWorkspaceError(
          err?.response?.data?.message ||
          err.message ||
          "Unable to load saved budget rows",
        );
      }

      setWorkspaceLoading(false);
    };

    loadWorkspace();
  }, [selectedYear, token]);

  const departments = useMemo(
    () =>
      getDepartmentCardsFromRows(workspaceRows).filter(
        (item) => item.key !== "executiveSummary",
      ),
    [workspaceRows],
  );
  const enabledDepartments = useMemo(
    () => buildEnabledDepartmentsMap(departmentDetails, workspaceRows),
    [departmentDetails, workspaceRows],
  );

  const selectedDepartmentCard =
    departments[selectedDepartment] ?? departments[0] ?? null;
  const selectedAssignmentValue =
    departmentDetails?.assignments?.[selectedDepartmentCard?.key];
  const selectedAssignments = Array.isArray(selectedAssignmentValue)
    ? selectedAssignmentValue
    : [];
  const previousWorkspaceDepartmentDetails =
    workspaceData?.previous_workspace?.department_details ?? null;
  const previousWorkspaceYear =
    workspaceData?.previous_workspace?.financial_year ?? "";
  const isDepartmentsLocked = Boolean(departmentDetails?.departments_locked);
  const syncedFromFinancialYear =
    departmentDetails?.synced_from_financial_year ?? "";
  const canSyncFromPriorYear =
    isMainUser &&
    !isDepartmentsLocked &&
    previousWorkspaceDepartmentDetails &&
    Object.keys(previousWorkspaceDepartmentDetails).length > 0;
  const canLockDepartments =
    isMainUser &&
    !isDepartmentsLocked &&
    Boolean(syncedFromFinancialYear);

  useEffect(() => {
    if (selectedDepartment >= departments.length) {
      setSelectedDepartment(0);
    }
  }, [departments.length, selectedDepartment]);

  useEffect(() => {
    setAssignmentError("");
    setAssignmentSuccess("");
    setAssignmentForm({ name: "", email: "" });
    setEditingAssignment(null);
    setEditForm({ name: "", password: "" });
  }, [selectedDepartment, selectedYear]);

  const updateAssignmentField = (field, value) => {
    setAssignmentForm((currentValue) => ({
      ...currentValue,
      [field]: value,
    }));
  };

  const handleToggleDepartment = async (departmentKey) => {
    if (!selectedYear) {
      return;
    }

    const workspace = workspaceData?.workspace ?? {};
    const nextDepartmentDetails = {
      ...(departmentDetails ?? {}),
      enabled_departments: {
        ...enabledDepartments,
        [departmentKey]: !(enabledDepartments[departmentKey] !== false),
      },
    };

    setToggling(true);
    setAssignmentError("");
    setAssignmentSuccess("");

    try {
      await saveWorkspace(token, selectedYear, {
        budget_rows: workspace.budget_rows ?? [],
        planner_details: workspace.planner_details ?? null,
        department_details: nextDepartmentDetails,
        bootstrap_metadata: workspace.bootstrap_metadata ?? null,
      });
      setDepartmentDetails(nextDepartmentDetails);
      setWorkspaceData((currentValue) => ({
        ...(currentValue ?? {}),
        workspace: {
          ...(currentValue?.workspace ?? {}),
          department_details: nextDepartmentDetails,
        },
      }));
      setAssignmentSuccess("Department visibility updated.");
    } catch (toggleError) {
      setAssignmentError(
        toggleError?.response?.data?.message ||
          toggleError.message ||
          "Unable to update department visibility.",
      );
    }

    setToggling(false);
  };

  const handleSyncPriorYearDepartments = async () => {
    if (
      !selectedYear ||
      !previousWorkspaceDepartmentDetails ||
      !previousWorkspaceYear
    ) {
      return;
    }

    const workspace = workspaceData?.workspace ?? {};
    const previousEnabledDepartments =
      previousWorkspaceDepartmentDetails?.enabled_departments &&
      typeof previousWorkspaceDepartmentDetails.enabled_departments === "object"
        ? previousWorkspaceDepartmentDetails.enabled_departments
        : {};
    const previousAssignments =
      previousWorkspaceDepartmentDetails?.assignments &&
      typeof previousWorkspaceDepartmentDetails.assignments === "object"
        ? Object.keys(previousWorkspaceDepartmentDetails.assignments).reduce(
            (agg, departmentKey) => {
              agg[departmentKey] = Array.isArray(
                previousWorkspaceDepartmentDetails.assignments[departmentKey],
              )
                ? previousWorkspaceDepartmentDetails.assignments[departmentKey].map(
                    (assignment) => ({ ...assignment }),
                  )
                : [];
              return agg;
            },
            {},
          )
        : {};
    const nextDepartmentDetails = {
      enabled_departments: buildEnabledDepartmentsMap(
        { enabled_departments: previousEnabledDepartments },
        workspaceRows,
      ),
      assignments: previousAssignments,
      departments_locked: false,
      synced_from_financial_year: previousWorkspaceYear,
    };

    setSyncingDepartments(true);
    setAssignmentError("");
    setAssignmentSuccess("");

    try {
      await saveWorkspace(token, selectedYear, {
        budget_rows: workspace.budget_rows ?? [],
        planner_details: workspace.planner_details ?? null,
        department_details: nextDepartmentDetails,
        bootstrap_metadata: workspace.bootstrap_metadata ?? null,
      });
      setDepartmentDetails(nextDepartmentDetails);
      setWorkspaceData((currentValue) => ({
        ...(currentValue ?? {}),
        workspace: {
          ...(currentValue?.workspace ?? {}),
          department_details: nextDepartmentDetails,
        },
      }));
      setAssignmentSuccess(
        `Departments synced from ${previousWorkspaceYear}.`,
      );
    } catch (syncError) {
      setAssignmentError(
        syncError?.response?.data?.message ||
          syncError.message ||
          "Unable to sync departments from the prior year.",
      );
    }

    setSyncingDepartments(false);
  };

  const handleLockDepartments = async () => {
    if (!selectedYear) {
      return;
    }

    const workspace = workspaceData?.workspace ?? {};
    const nextDepartmentDetails = {
      ...(departmentDetails ?? {}),
      departments_locked: true,
    };

    setLockingDepartments(true);
    setAssignmentError("");
    setAssignmentSuccess("");

    try {
      await saveWorkspace(token, selectedYear, {
        budget_rows: workspace.budget_rows ?? [],
        planner_details: workspace.planner_details ?? null,
        department_details: nextDepartmentDetails,
        bootstrap_metadata: workspace.bootstrap_metadata ?? null,
      });
      setDepartmentDetails(nextDepartmentDetails);
      setWorkspaceData((currentValue) => ({
        ...(currentValue ?? {}),
        workspace: {
          ...(currentValue?.workspace ?? {}),
          department_details: nextDepartmentDetails,
        },
      }));
      setAssignmentSuccess("Departments locked for this year.");
    } catch (lockError) {
      setAssignmentError(
        lockError?.response?.data?.message ||
          lockError.message ||
          "Unable to lock departments for this year.",
      );
    }

    setLockingDepartments(false);
  };

  const handleAssignPerson = async (event) => {
    event.preventDefault();

    if (!selectedYear || !selectedDepartmentCard) {
      return;
    }

    setAssigning(true);
    setAssignmentError("");
    setAssignmentSuccess("");

    try {
      const response = await assignDepartmentPerson(token, selectedYear, {
        department_key: selectedDepartmentCard.key,
        name: assignmentForm.name.trim(),
        email: assignmentForm.email.trim(),
      });

      setDepartmentDetails(response?.data?.department_details ?? null);
      setAssignmentForm({ name: "", email: "" });
      setAssignmentSuccess("Person assigned to department.");
    } catch (assignError) {
      setAssignmentError(
        assignError?.response?.data?.message ||
          assignError.message ||
          "Unable to assign person to department.",
      );
    }

    setAssigning(false);
  };

  const openEditAssignment = (person) => {
    setAssignmentError("");
    setAssignmentSuccess("");
    setEditingAssignment(person);
    setEditForm({
      name: person.name ?? "",
      password: "",
    });
  };

  const handleUpdateAssignment = async (event) => {
    event.preventDefault();

    if (!selectedYear || !editingAssignment?.user_id) {
      return;
    }

    setEditing(true);
    setAssignmentError("");
    setAssignmentSuccess("");

    try {
      const payload = { name: editForm.name.trim() };
      if (editForm.password.trim()) {
        payload.password = editForm.password.trim();
      }

      const response = await updateDepartmentPerson(
        token,
        selectedYear,
        editingAssignment.user_id,
        payload,
      );

      setDepartmentDetails(response?.data?.department_details ?? null);
      setEditingAssignment(null);
      setEditForm({ name: "", password: "" });
      setAssignmentSuccess("Assigned user updated.");
    } catch (updateError) {
      setAssignmentError(
        updateError?.response?.data?.message ||
          updateError.message ||
          "Unable to update assigned user.",
      );
    }

    setEditing(false);
  };

  const handleRemoveAssignment = async (person) => {
    const confirmed = window.confirm(
      `Remove ${person.name} from this budget department?`,
    );

    if (!confirmed || !selectedYear || !person?.user_id) {
      return;
    }

    setAssignmentError("");
    setAssignmentSuccess("");

    try {
      const response = await removeDepartmentPerson(
        token,
        selectedYear,
        person.user_id,
      );
      setDepartmentDetails(response?.data?.department_details ?? null);
      setAssignmentSuccess("Assigned user removed.");
    } catch (removeError) {
      setAssignmentError(
        removeError?.response?.data?.message ||
          removeError.message ||
          "Unable to remove assigned user.",
      );
    }
  };

  return (
    <BudgetModulePageShell
      title="Budget Module Departments"
      description=""
      yearCards={yearCards}
      selectedYear={selectedYear}
      onSelectYear={setSelectedYear}
    >
      {error ? <div className="alert alert--error">{error}</div> : null}
      {workspaceError ? (
        <div className="alert alert--error">{workspaceError}</div>
      ) : null}

      {loading || workspaceLoading ? (
        <AppLoader label="Loading departments..." />
      ) : !selectedYear ? (
        <p>No budget years are available yet. Start from Bootstrap and Mapping.</p>
      ) : (
        <div className="base-container budget-departments-management">
          {!workspaceRows.length ? (
            <p className="budget-module-departments-empty">
              {getPlannerEmptyStateMessage(
                Boolean(workspaceData?.accounting_snapshot),
              )}
            </p>
          ) : departments.length === 0 ? (
            <p className="budget-module-departments-empty">
              No departments found for this year.
            </p>
          ) : (
            <div className="budget-departments-layout">
              <div className="bp-listContainer budget-departments-list">
                {departments.map((item, index) => (
                  <BudgetModuleListCard
                    key={item.key}
                    title={item.name}
                    isSelected={selectedDepartment === index}
                    onClick={() => setSelectedDepartment(index)}
                  />
                ))}
              </div>

              <div className="budget-departments-detail">
                <div className="budget-departments-detailHeader">
                  <div>
                    <span>Department</span>
                    <strong>{selectedDepartmentCard?.name}</strong>
                  </div>
                  <small>{selectedAssignments.length} assigned</small>
                </div>

                {isMainUser ? (
                  <div className="budget-departments-syncRow">
                    {canSyncFromPriorYear ? (
                      <button
                        type="button"
                        className="button-secondary"
                        disabled={syncingDepartments || lockingDepartments}
                        onClick={handleSyncPriorYearDepartments}
                      >
                        {syncingDepartments ? (
                          <ButtonLoader label="Syncing..." />
                        ) : (
                          `Sync departments from ${previousWorkspaceYear}`
                        )}
                      </button>
                    ) : null}

                    {canLockDepartments ? (
                      <button
                        type="button"
                        className="button-secondary"
                        disabled={lockingDepartments || syncingDepartments}
                        onClick={handleLockDepartments}
                      >
                        {lockingDepartments ? (
                          <ButtonLoader label="Locking..." />
                        ) : (
                          "Lock departments"
                        )}
                      </button>
                    ) : null}

                    {isDepartmentsLocked ? (
                      <span className="budget-departments-lockedBadge">
                        Departments locked
                      </span>
                    ) : null}
                  </div>
                ) : null}

                <div className="budget-departments-statusRow">
                  <span>
                    {selectedDepartmentCard &&
                    enabledDepartments[selectedDepartmentCard.key] !== false
                      ? "Visible in planner"
                      : "Hidden from planner"}
                  </span>
                  <button
                    type="button"
                    className="button-secondary"
                    disabled={toggling || !selectedDepartmentCard}
                    onClick={() =>
                      selectedDepartmentCard
                        ? handleToggleDepartment(selectedDepartmentCard.key)
                        : null
                    }
                  >
                    {selectedDepartmentCard &&
                    enabledDepartments[selectedDepartmentCard.key] !== false
                      ? "Switch off"
                      : "Switch on"}
                  </button>
                </div>

                {assignmentError ? (
                  <div className="alert alert--error">{assignmentError}</div>
                ) : null}
                {assignmentSuccess ? (
                  <div className="alert alert--success">{assignmentSuccess}</div>
                ) : null}

                <div className="budget-departments-assignedList">
                  {selectedAssignments.length === 0 ? (
                    <p className="budget-module-departments-empty">
                      No people assigned to this department yet.
                    </p>
                  ) : (
                    selectedAssignments.map((person) => (
                      <div
                        className="budget-departments-person"
                        key={`${person.user_id}-${person.email}`}
                      >
                        <div>
                          <strong>{person.name}</strong>
                          <span>{person.email}</span>
                        </div>
                        <div className="budget-departments-personActions">
                          <small>Accountant</small>
                          <button
                            type="button"
                            className="button-secondary"
                            onClick={() => openEditAssignment(person)}
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            className="button-secondary"
                            onClick={() => handleRemoveAssignment(person)}
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>

                {editingAssignment ? (
                  <form
                    className="budget-departments-form"
                    onSubmit={handleUpdateAssignment}
                  >
                    <label className="bp-controlField">
                      <span className="bp-controlLabel">Edit name</span>
                      <input
                        className="bp-controlInput"
                        value={editForm.name}
                        onChange={(event) =>
                          setEditForm((currentValue) => ({
                            ...currentValue,
                            name: event.target.value,
                          }))
                        }
                        required
                        minLength={3}
                        maxLength={64}
                      />
                    </label>
                    <label className="bp-controlField">
                      <span className="bp-controlLabel">New password</span>
                      <input
                        className="bp-controlInput"
                        type="password"
                        value={editForm.password}
                        onChange={(event) =>
                          setEditForm((currentValue) => ({
                            ...currentValue,
                            password: event.target.value,
                          }))
                        }
                        placeholder="Leave blank to keep current password"
                        minLength={3}
                        maxLength={32}
                      />
                    </label>
                    <div className="budget-departments-actions">
                      <button
                        type="button"
                        className="button-secondary"
                        disabled={editing}
                        onClick={() => setEditingAssignment(null)}
                      >
                        Cancel
                      </button>
                      <button type="submit" disabled={editing}>
                        {editing ? (
                          <ButtonLoader label="Saving..." />
                        ) : (
                          "Save user"
                        )}
                      </button>
                    </div>
                  </form>
                ) : null}

                <form
                  className="budget-departments-form"
                  onSubmit={handleAssignPerson}
                >
                  <label className="bp-controlField">
                    <span className="bp-controlLabel">Name</span>
                    <input
                      className="bp-controlInput"
                      value={assignmentForm.name}
                      onChange={(event) =>
                        updateAssignmentField("name", event.target.value)
                      }
                      placeholder="Enter name"
                      required
                      minLength={3}
                      maxLength={64}
                    />
                  </label>
                  <label className="bp-controlField">
                    <span className="bp-controlLabel">Email</span>
                    <input
                      className="bp-controlInput"
                      type="email"
                      value={assignmentForm.email}
                      onChange={(event) =>
                        updateAssignmentField("email", event.target.value)
                      }
                      placeholder="Enter email"
                      required
                    />
                  </label>
                  <div className="budget-departments-actions">
                    <button
                      type="submit"
                      disabled={assigning || !selectedDepartmentCard}
                    >
                      {assigning ? (
                        <ButtonLoader label="Assigning..." />
                      ) : (
                        "Assign person"
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      )}
    </BudgetModulePageShell>
  );
};

export default BudgetModuleDepartmentsPage;
